import { Scene, GameObjects } from 'phaser';
import { MenuButton } from '../ui/MenuButton';
import { storage } from '../core/Storage';
import { weaponShopSystem, SlotType, WeaponConfig } from '../systems';
import { FloatingNotification } from '../ui/FloatingNotification';
import { initLocalization, t } from '../i18n/Localization';

interface WeaponCardElements {
  container: GameObjects.Container;
  checkbox: GameObjects.Graphics;
  checkmark: GameObjects.Text;
  ammoText: GameObjects.Text | null;
  buyBtn: GameObjects.Container | null;
}

export class WeaponWorkshop extends Scene {
  private levelToStart: number = 1;
  private biomatter: number = 0;

  // UI Elements
  private biomatterText: GameObjects.Text | null = null;
  private weaponCards: Map<string, WeaponCardElements> = new Map();
  private contentContainer: GameObjects.Container | null = null;

  // Scroll state
  private scrollY: number = 0;
  private maxScrollY: number = 0;
  private contentHeight: number = 0;
  private viewportHeight: number = 0;
  private viewportTop: number = 0;

  constructor() {
    super('WeaponWorkshop');
  }

  init(data: { level?: number }): void {
    this.levelToStart = data.level ?? 1;
    this.scrollY = 0;
  }

  async create(): Promise<void> {
    initLocalization(this);
    await weaponShopSystem.init();
    this.biomatter = await storage.getBiomatter();
    this.createUI();
    this.setupScrolling();
  }

  private createUI(): void {
    const { width, height } = this.scale;

    // Background
    const bg = this.add.image(width / 2, height / 2, 'background');
    bg.setDisplaySize(width, height);
    bg.setAlpha(0.3);

    // Header
    this.createHeader(width);

    // Main content area with weapons grouped by slot
    this.createWeaponsList(width, height);

    // Footer buttons
    this.createFooter(width, height);
  }

  private createHeader(width: number): void {
    // Title
    this.add.text(width / 2, 35, t('weapon_workshop.title'), {
      fontFamily: 'Arial Black',
      fontSize: '36px',
      color: '#00ff00',
      stroke: '#003300',
      strokeThickness: 6,
    }).setOrigin(0.5);

    // Level indicator
    this.add.text(width / 2, 70, t('weapon_workshop.preparing').replace('{level}', `${this.levelToStart}`), {
      fontFamily: 'Arial',
      fontSize: '18px',
      color: '#88ff88',
    }).setOrigin(0.5);

    // Biomatter display
    this.add.text(width - 200, 35, t('weapon_workshop.biomatter_label'), {
      fontFamily: 'Arial Black',
      fontSize: '16px',
      color: '#aaaaaa',
    }).setOrigin(0, 0.5);

    this.biomatterText = this.add.text(width - 30, 35, this.biomatter.toLocaleString(), {
      fontFamily: 'Arial Black',
      fontSize: '24px',
      color: '#00ff00',
    }).setOrigin(1, 0.5);
  }

  private createWeaponsList(width: number, height: number): void {
    const panelWidth = Math.min(900, width - 100);
    const panelX = width / 2;
    this.viewportTop = 100;
    this.viewportHeight = height - 200;

    // Panel background
    this.add.rectangle(panelX, this.viewportTop + this.viewportHeight / 2, panelWidth, this.viewportHeight, 0x111111, 0.9)
      .setStrokeStyle(2, 0x333333);

    // Create mask for scrolling
    const maskShape = this.make.graphics({ x: 0, y: 0 });
    maskShape.fillStyle(0xffffff);
    maskShape.fillRect(panelX - panelWidth / 2, this.viewportTop, panelWidth, this.viewportHeight);
    const mask = maskShape.createGeometryMask();

    // Content container (will be scrolled)
    this.contentContainer = this.add.container(panelX, this.viewportTop + 20);
    this.contentContainer.setMask(mask);

    // Build content
    let yOffset = 0;
    const cardWidth = panelWidth - 40;
    const slots: { type: SlotType; key: string; name: string }[] = [
      { type: 'tractor', key: 'Z', name: t('slot.tractor') },
      { type: 'gun', key: 'X', name: t('slot.gun') },
      { type: 'bomb', key: 'C', name: t('slot.bomb') },
    ];

    slots.forEach((slotInfo) => {
      // Slot header
      yOffset += this.createSlotHeader(yOffset, cardWidth, slotInfo.key, slotInfo.name);

      // Weapons for this slot
      const weapons = weaponShopSystem.getDiscoveredForSlot(slotInfo.type);
      weapons.forEach((weapon) => {
        yOffset += this.createWeaponCard(yOffset, cardWidth, weapon);
      });

      yOffset += 20; // Gap between slots
    });

    this.contentHeight = yOffset;
    this.maxScrollY = Math.max(0, this.contentHeight - this.viewportHeight + 60);
  }

  private createSlotHeader(y: number, width: number, key: string, name: string): number {
    const height = 35;

    // Header line with slot name
    const headerBg = this.add.rectangle(0, y + height / 2, width, height, 0x222222)
      .setStrokeStyle(1, 0x444444);

    const headerText = this.add.text(0, y + height / 2, `═══════════ [${key}] ${name} ═══════════`, {
      fontFamily: 'Arial Black',
      fontSize: '16px',
      color: '#00ff00',
    }).setOrigin(0.5);

    this.contentContainer!.add([headerBg, headerText]);

    return height + 8;
  }

  private createWeaponCard(y: number, width: number, weapon: WeaponConfig): number {
    const height = 70;
    const isInLoadout = weaponShopSystem.isInLoadout(weapon.slot, weapon.id);
    const loadout = weaponShopSystem.getLoadout(weapon.slot);
    const isSlotFull = loadout.equipped.length >= weaponShopSystem.getMaxWeaponsPerSlot();
    const isOnlyWeaponInSlot = loadout.equipped.length === 1 && isInLoadout;

    const container = this.add.container(0, y);

    // Card background
    const bgColor = isInLoadout ? 0x003300 : 0x1a1a1a;
    const bg = this.add.rectangle(0, height / 2, width, height - 4, bgColor)
      .setStrokeStyle(1, isInLoadout ? 0x00ff00 : 0x333333);

    container.add(bg);

    // Checkbox (toggle)
    const checkboxX = -width / 2 + 30;
    const checkboxY = height / 2;
    const checkboxSize = 22;

    const checkbox = this.add.graphics();
    this.drawCheckbox(checkbox, checkboxX, checkboxY, checkboxSize, isInLoadout);

    // Checkmark
    const checkmark = this.add.text(checkboxX, checkboxY, isInLoadout ? '✓' : '', {
      fontFamily: 'Arial',
      fontSize: '16px',
      color: '#00ff00',
    }).setOrigin(0.5);

    container.add([checkbox, checkmark]);

    // Weapon name
    const nameText = this.add.text(-width / 2 + 60, height / 2 - 12, weapon.name, {
      fontFamily: 'Arial Black',
      fontSize: '16px',
      color: isInLoadout ? '#00ff00' : '#ffffff',
    }).setOrigin(0, 0.5);

    // Description
    const descValue = weapon.descriptionKey ? t(weapon.descriptionKey) : weapon.description;
    const descText = this.add.text(-width / 2 + 60, height / 2 + 10, descValue ?? '', {
      fontFamily: 'Arial',
      fontSize: '12px',
      color: '#888888',
    }).setOrigin(0, 0.5);

    container.add([nameText, descText]);

    // Ammo section (right side)
    let ammoText: GameObjects.Text | null = null;
    let buyBtn: GameObjects.Container | null = null;

    if (weapon.ammoType === 'infinite') {
      const infText = this.add.text(width / 2 - 20, height / 2, `∞ ${t('weapon_workshop.infinite')}`, {
        fontFamily: 'Arial',
        fontSize: '14px',
        color: '#00ff00',
      }).setOrigin(1, 0.5);
      container.add(infText);
    } else {
      const ammo = weaponShopSystem.getAmmo(weapon.id);
      const packSize = weapon.packSize ?? 0;
      const packCost = weapon.packCost ?? 0;
      const lowAmmo = ammo < 20;

      // Ammo count
      ammoText = this.add.text(width / 2 - 150, height / 2, t('weapon_workshop.ammo').replace('{ammo}', `${ammo}`), {
        fontFamily: 'Arial',
        fontSize: '14px',
        color: ammo === 0 ? '#ff4444' : (lowAmmo ? '#ffaa00' : '#ffff00'),
      }).setOrigin(1, 0.5);

      container.add(ammoText);

      // Buy button
      buyBtn = this.createInlineBuyButton(
        width / 2 - 75,
        height / 2,
        packSize,
        packCost,
        weapon.id
      );
      container.add(buyBtn);
    }

    // Make checkbox interactive
    const hitArea = this.add.rectangle(checkboxX, checkboxY, checkboxSize + 10, checkboxSize + 10, 0x000000, 0);
    hitArea.setInteractive({ useHandCursor: true });
    container.add(hitArea);

    // Disable if slot full and not in loadout, or if only weapon in slot
    const canToggle = !isOnlyWeaponInSlot && (!isSlotFull || isInLoadout);

    if (canToggle) {
      hitArea.on('pointerover', () => {
        checkbox.clear();
        this.drawCheckbox(checkbox, checkboxX, checkboxY, checkboxSize, isInLoadout, true);
      });

      hitArea.on('pointerout', () => {
        checkbox.clear();
        this.drawCheckbox(checkbox, checkboxX, checkboxY, checkboxSize, isInLoadout, false);
      });

      hitArea.on('pointerdown', () => {
        this.toggleWeaponInLoadout(weapon);
      });
    } else {
      hitArea.disableInteractive();
      checkbox.alpha = 0.5;
      checkmark.alpha = 0.5;
    }

    // Hover effect on card background
    bg.setInteractive({ useHandCursor: false });
    bg.on('pointerover', () => {
      if (!isInLoadout) {
        bg.setFillStyle(0x2a2a2a);
      }
    });
    bg.on('pointerout', () => {
      bg.setFillStyle(bgColor);
    });

    this.contentContainer!.add(container);

    // Store references for updates
    this.weaponCards.set(weapon.id, {
      container,
      checkbox,
      checkmark,
      ammoText,
      buyBtn,
    });

    return height;
  }

  private drawCheckbox(graphics: GameObjects.Graphics, x: number, y: number, size: number, checked: boolean, hover: boolean = false): void {
    const half = size / 2;

    // Border
    graphics.lineStyle(2, hover ? 0x00ff00 : (checked ? 0x00aa00 : 0x666666));
    graphics.strokeRect(x - half, y - half, size, size);

    // Fill
    if (checked) {
      graphics.fillStyle(0x004400, 1);
      graphics.fillRect(x - half + 2, y - half + 2, size - 4, size - 4);
    }
  }

  private createInlineBuyButton(x: number, y: number, packSize: number, packCost: number, weaponId: string): GameObjects.Container {
    const container = this.add.container(x, y);
    const canBuy = this.biomatter >= packCost;

    // Button background
    const btnWidth = 120;
    const btnHeight = 28;
    const bg = this.add.rectangle(0, 0, btnWidth, btnHeight, canBuy ? 0x004400 : 0x333333)
      .setStrokeStyle(1, canBuy ? 0x00aa00 : 0x444444);

    // Button text
    const text = this.add.text(0, 0, t('weapon_workshop.pack')
      .replace('{size}', `${packSize}`)
      .replace('{cost}', `${packCost}`), {
      fontFamily: 'Arial',
      fontSize: '12px',
      color: canBuy ? '#00ff00' : '#666666',
    }).setOrigin(0.5);

    container.add([bg, text]);
    container.setSize(btnWidth, btnHeight);

    if (canBuy) {
      bg.setInteractive({ useHandCursor: true });

      bg.on('pointerover', () => {
        bg.setFillStyle(0x006600);
      });

      bg.on('pointerout', () => {
        bg.setFillStyle(0x004400);
      });

      bg.on('pointerdown', () => {
        this.buyAmmo(weaponId);
      });
    }

    return container;
  }

  private setupScrolling(): void {
    // Mouse wheel scrolling
    this.input.on('wheel', (_pointer: Phaser.Input.Pointer, _gameObjects: GameObjects.GameObject[], _deltaX: number, deltaY: number) => {
      this.scrollY = Phaser.Math.Clamp(this.scrollY + deltaY * 0.5, 0, this.maxScrollY);
      this.updateScrollPosition();
    });

    // Touch/drag scrolling could be added here
  }

  private updateScrollPosition(): void {
    if (this.contentContainer) {
      this.contentContainer.y = this.viewportTop + 20 - this.scrollY;
    }
  }

  private createFooter(width: number, height: number): void {
    // Start Level button
    new MenuButton({
      scene: this,
      x: width / 2 + 100,
      y: height - 50,
      text: t('weapon_workshop.start_level').replace('{level}', `${this.levelToStart}`),
      width: 280,
      height: 60,
      onClick: () => this.startLevel(),
    });

    // Back button
    new MenuButton({
      scene: this,
      x: width / 2 - 100,
      y: height - 50,
      text: t('ui.back'),
      width: 160,
      height: 60,
      fontSize: 20,
      onClick: () => this.scene.start('LevelSelect'),
    });
  }

  // ========== ACTIONS ==========

  private async toggleWeaponInLoadout(weapon: WeaponConfig): Promise<void> {
    const isInLoadout = weaponShopSystem.isInLoadout(weapon.slot, weapon.id);

    if (isInLoadout) {
      await weaponShopSystem.removeFromLoadout(weapon.slot, weapon.id);
    } else {
      await weaponShopSystem.addToLoadout(weapon.slot, weapon.id);
    }

    this.updateWeaponCardState(weapon.id);
  }

  private async buyAmmo(weaponId: string): Promise<void> {
    const result = await weaponShopSystem.buyAmmo(weaponId);
    if (result.success) {
      this.biomatter = await storage.getBiomatter();
      this.updateBiomatterDisplay();
      this.updateWeaponCardAmmo(weaponId, result.newTotal);
      FloatingNotification.success(
        this,
        t('weapon_workshop.ammo_added').replace('{ammo}', `${result.ammoAdded}`),
        { isHUD: true, fontSize: 28 }
      );
    } else {
      FloatingNotification.error(this, t('weapon_workshop.not_enough_biomatter'), { isHUD: true, fontSize: 28 });
    }
  }

  // ========== UI UPDATES (No scene restart) ==========

  private updateBiomatterDisplay(): void {
    if (this.biomatterText) {
      this.biomatterText.setText(this.biomatter.toLocaleString());

      // Flash effect
      this.tweens.add({
        targets: this.biomatterText,
        scaleX: 1.2,
        scaleY: 1.2,
        duration: 100,
        yoyo: true,
      });
    }
  }

  private updateWeaponCardState(weaponId: string): void {
    // For state changes, we need to rebuild the card
    // This is simpler than trying to update all visual elements
    this.scene.restart({ level: this.levelToStart });
  }

  private updateWeaponCardAmmo(weaponId: string, newAmmo: number): void {
    const card = this.weaponCards.get(weaponId);
    if (!card || !card.ammoText) return;

    const lowAmmo = newAmmo < 20;
    card.ammoText.setText(t('weapon_workshop.ammo').replace('{ammo}', `${newAmmo}`));
    card.ammoText.setColor(newAmmo === 0 ? '#ff4444' : (lowAmmo ? '#ffaa00' : '#ffff00'));

    // Flash effect on ammo text
    this.tweens.add({
      targets: card.ammoText,
      scaleX: 1.3,
      scaleY: 1.3,
      duration: 100,
      yoyo: true,
    });
  }

  private startLevel(): void {
    const state = weaponShopSystem.getState();
    if (!state) return;

    this.scene.start('LevelPreloader', {
      levelId: `level${this.levelToStart}`,
      levelNum: this.levelToStart,
      weaponState: state,
    });
  }
}
